<template>
  <WizardContent
    :show-sidebar="false"
    class="wizard-wrapper"
  >
    <GateHeader>
      <template #logo>
        <JobNavIcon />
      </template>
      <template #page-title>
        {{ heading }}
      </template>
    </GateHeader>
    <p class="text-body-2 mb-4">
      Your JobNav account needs a new password. Please set one below.
    </p>
    <v-form
      ref="form"
      v-model="valid"
      class="form"
      @submit.prevent="submitForm"
    >
      <DpplTextField
        v-model="password"
        label="Password"
        :rules="passwordRules"
        type="password"
        autocomplete="off"
        has-details
        validate-on="blur"
        data-sentry-mask
      />
      <DpplTextField
        v-model="confirmPassword"
        label="Confirm Password"
        :rules="confirmPasswordRules"
        type="password"
        autocomplete="off"
        has-details
        validate-on="blur"
        data-sentry-mask
      />
      <DpplSignupFormButton
        type="submit"
        class="mt-1"
        block
        :loading="loading"
      >
        Submit
      </DpplSignupFormButton>
    </v-form>
  </WizardContent>
</template>

<script>
import { mapActions } from 'pinia';
import WizardContent from '@/components/shared/WizardContent.vue';
import GateHeader from '@/components/shared/gates/GateHeader.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSignupFormButton from '@/components/datapeople/DpplSignupFormButton.vue';
import { useWizard } from '@/stores/wizard/wizard';
import { signupSetPassword } from '@/api/signup';
import validation from '@/helpers/validation';
import JobNavIcon from '@/assets/jobnav-payscale-icon-logo.svg?component';

export default {
  name: 'CreatePassword',
  components: {
    WizardContent,
    GateHeader,
    DpplTextField,
    DpplSignupFormButton,
    JobNavIcon,
  },
  emits: ['track-page-load'],
  data() {
    return {
      reason: this.$route.query.reason || null,
      passwordResetToken: this.$route.query.token || null,
      email: this.$route.query.email || '',
      password: '',
      confirmPassword: '',
      passwordRules: validation.passwordRules(),
      confirmPasswordRules: [(v) => v === this.password || 'Passwords must match'],
      valid: false,
      loading: false,
      firstName: this.$route.query.firstName || 'Friend',
    };
  },
  computed: {
    heading() {
      if (this.reason === 'expired') {
        return 'Your JobNav password has expired.';
      }
      return 'Create a Password';
    },
  },
  async created() {
    if (!this.passwordResetToken) {
      await this.$router.push({
        name: 'signup',
      });
    }
  },
  beforeCreate() {
    this.$emit('track-page-load', { page: 'Create Password' });
  },
  methods: {
    ...mapActions(useWizard, ['updateFlash']),
    async submitForm() {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await signupSetPassword({
            passwordResetToken: this.passwordResetToken,
            email: this.email,
            password: this.password,
            confirmPassword: this.confirmPassword,
          });
          if (responseJSON.success === true) {
            this.$router.replace({ name: 'login', query: { passwordChanged: 'true' } });
          }
        }
      } catch (e) {
        if (!e.message || e.message.charAt(0) !== '{') {
          this.updateFlash({ message: `Create password error: ${e.message}` });
          throw e;
        }
        const responseJSON = JSON.parse(e.message);
        if (responseJSON.errors && responseJSON.errors.sso_mandatory) {
          if (responseJSON.errors.is_google) {
            window.location.href = '/signup/google';
          } else if (responseJSON.errors.is_microsoft) {
            window.location.href = '/signup/microsoft';
          } else {
            window.location.href = `/login/${responseJSON.errors.provider}`;
          }
          return;
        }
        if (
          responseJSON.errors
          && responseJSON.errors.password
          && responseJSON.errors.password
            .includes('New password can not be one of your last 10 passwords.')
        ) {
          this.updateFlash({ message: 'New password can not be one of your last 10 passwords.' });
        } else if (
          responseJSON.errors
          && responseJSON.errors.password_reset_token
          && responseJSON.errors.password_reset_token.includes('Invalid Token')
        ) {
          this.updateFlash({ message: 'Password reset link expired. Please request a new password reset link' });
        } else {
          this.updateFlash({ message: 'Something went wrong' });
        }
        throw e;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.form {
  width: 100%;
}

.wizard-wrapper {
  min-width: 330px;
  width: 100%;
  align-items: center;
  justify-content: center;
}

:deep(.wizard-flash-message) {
  border-top-right-radius: 0px !important;
}

:deep(.slot-flash-message.full-width) {
  width: 450px;
}
</style>
